import { paymentStatementApi } from './base';

// ROC-87: pricing limits on a template and the approval queue for quotes outside them.
export type LimitedRate =
	| 'discount_rate'
	| 'authorization_fee'
	| 'monthly_fee'
	| 'batch_fee';

export interface RateLimit {
	floor?: number | null;
	ceiling?: number | null;
	rep_min?: number | null;
	rep_max?: number | null;
}

export type PricingLimits = Partial<Record<LimitedRate, RateLimit | null>>;

export type PricingLimitAction = 'route' | 'block';

export type ApprovalStatus = 'pending' | 'approved' | 'sent_back' | 'rejected';

export type ViolationKind =
	| 'below_floor'
	| 'above_ceiling'
	| 'outside_rep_range';

export interface LimitViolation {
	rate: LimitedRate;
	kind: ViolationKind;
	proposed: number;
	limit: number;
	floor?: number | null;
}

export interface FilePricingLimits {
	template_id: string | null;
	template_name: string | null;
	limits: PricingLimits | null;
	action: PricingLimitAction;
	can_approve: boolean;
	pending_approval?: boolean;
}

export interface ApprovalRequest {
	id: string;
	statement_file_id: string;
	merchant_name: string;
	template_id: string | null;
	template_name: string | null;
	status: ApprovalStatus;
	violations: LimitViolation[];
	requested_by: string;
	requested_by_name: string;
	requested_at: string;
	decided_by: string | null;
	decided_by_name: string | null;
	decided_at: string | null;
	decision_note: string | null;
	request_reason?: string | null;
	required_approver?: 'pricing' | 'manager' | 'admin';
}

export const pricingApprovalsApi = paymentStatementApi.injectEndpoints({
	endpoints: (builder) => ({
		getPricingLimitSettings: builder.query<
			{ action: PricingLimitAction },
			void
		>({
			query: () => '/pricing-limit-settings',
			providesTags: ['PricingApprovals'],
		}),
		updatePricingLimitSettings: builder.mutation<
			{ action: PricingLimitAction },
			{ action: PricingLimitAction }
		>({
			query: (body) => ({
				url: '/pricing-limit-settings',
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['PricingApprovals'],
		}),
		getFilePricingLimits: builder.query<FilePricingLimits, string>({
			query: (fileId) => `/statement-files/${fileId}/pricing-limits`,
			providesTags: ['PricingApprovals'],
		}),
		listPricingApprovals: builder.query<
			{ data: ApprovalRequest[]; total: number },
			void
		>({
			query: () => '/pricing-approvals?limit=100',
			providesTags: ['PricingApprovals'],
		}),
		decidePricingApproval: builder.mutation<
			ApprovalRequest,
			{ id: string; status: Exclude<ApprovalStatus, 'pending'>; note?: string }
		>({
			query: ({ id, ...body }) => ({
				url: `/pricing-approvals/${id}/decisions`,
				method: 'POST',
				body,
			}),
			invalidatesTags: ['PricingApprovals'],
		}),
	}),
});

export const {
	useGetPricingLimitSettingsQuery,
	useUpdatePricingLimitSettingsMutation,
	useGetFilePricingLimitsQuery,
	useListPricingApprovalsQuery,
	useDecidePricingApprovalMutation,
} = pricingApprovalsApi;

// Same rule as the API (services/pricing_approval_service.py): floor and ceiling bind
// everyone, the rep range binds people who cannot approve. The API check is the real one.
export const findViolations = (
	limits: PricingLimits,
	proposed: Partial<Record<LimitedRate, number[]>>,
	isApprover: boolean,
): LimitViolation[] => {
	const out: LimitViolation[] = [];
	(Object.keys(limits) as LimitedRate[]).forEach((rate) => {
		const l = limits[rate];
		if (!l) return;
		const floor = l.floor ?? null;
		for (const value of proposed[rate] ?? []) {
			const broken: [ViolationKind, number] | null =
				l.floor != null && value < l.floor
					? ['below_floor', l.floor]
					: l.ceiling != null && value > l.ceiling
						? ['above_ceiling', l.ceiling]
						: !isApprover && l.rep_min != null && value < l.rep_min
							? ['outside_rep_range', l.rep_min]
							: !isApprover && l.rep_max != null && value > l.rep_max
								? ['outside_rep_range', l.rep_max]
								: null;
			if (broken) {
				out.push({
					rate,
					kind: broken[0],
					proposed: value,
					limit: broken[1],
					floor,
				});
				break;
			}
		}
	});
	return out;
};

export const LIMITED_RATES: {
	rate: LimitedRate;
	label: string;
	unit: '%' | '$';
}[] = [
	{ rate: 'discount_rate', label: 'Discount rate', unit: '%' },
	{ rate: 'authorization_fee', label: 'Authorization fee', unit: '$' },
	{ rate: 'monthly_fee', label: 'Monthly fees (total)', unit: '$' },
	{ rate: 'batch_fee', label: 'Batch fee', unit: '$' },
];

export const formatLimitValue = (rate: LimitedRate, value: number): string =>
	rate === 'discount_rate' ? `${value.toFixed(2)}%` : `$${value.toFixed(2)}`;
